import { Intro, DataTable, Pill, Image, PopOverMenu } from 'components/commons';
import { useApi, useFilter, useMount } from 'hooks';
import useModal from 'hooks/useModal';
import { DefaultProfile } from 'images';
import React, { useCallback, useMemo } from 'react';
import { formatName, prettifyCustomerStatus } from 'utils';
import { customerColumns } from './customer-columns';
import CustomerFilter from './customer-filter';
import { customerFilterState } from './customer-filter.state';
import CustomerForm from './customer-form';
import styles from './customer.module.scss';
import locale from 'localization';
import UserRole from 'enums/user-role';
import CustomerStatus from 'enums/driver-status';
import { getCustomers } from 'apis/customer.api';
import { DateTime, Path } from 'enums';
import moment from 'moment';
import CustomerAccountForm from './customer-account-form';
import { generateReport } from 'apis/generate-report.api';
import { useHistory } from 'react-router-dom';

const CustomerModule = () => {
  const history = useHistory();
  const statusModal = useModal();

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(customerFilterState());
  const customerFormModal = useModal();
  const customerAccountFormModal = useModal();

  const {
    request: getCustomerRequest,
    loading: fetchingCustomer,
    result: getCustomerResult = { customers: [], count: 0 }
  } = useApi({
    api: getCustomers,
    pageError: true
  });

  useMount(() => {
    fetchCustomers(requestState);
  });

  const { request: generateReportRequest, loading: fetchingReport } = useApi({
    api: generateReport,
    pageError: true
  });

  const fetchCustomers = useCallback(
    (rs) => {
      getCustomerRequest(submitFilter({ ...rs }));
    },
    [getCustomerRequest, submitFilter]
  );

  const changeStatusCb = useCallback(
    (customerId, driverName, status) => {
      statusModal.show({ status, customerId, driverName });
    },
    [statusModal]
  );

  const preparedDriversData = useMemo(() => {
    const { customers } = getCustomerResult;
    if (customers.length > 0) {
      const preparedData = customers.map((c) => {
        const map = new Map();
        const customerName = formatName(c.firstName, c.lastName);
        map.set('avatar', <Image src={DefaultProfile} className={styles.avatar} />);
        map.set('name', customerName);
        map.set('cifNumber', c.cifNumber);
        map.set('mobileNumber', c.mobileNumber);
        map.set('emailAddress', c.emailAddress);
        map.set('type', c.type);
        map.set(
          'status',
          <Pill grass={c.status === CustomerStatus.Active} cheddar={c.status === CustomerStatus.Inactive}>
            {prettifyCustomerStatus(c.status)}
          </Pill>
        );
        let options = [];
        let editDetails;
        let deactivateUser;

        editDetails = {
          content: locale.editDetails,
          onClick: () => {
            customerFormModal.show({
              title: locale.editDetails,
              actionText: locale.save,
              create: false,
              customerId: c.customerId,
              initialState: {
                cifNumber: {
                  value: c.cifNumber
                },
                firstName: {
                  value: c.firstName
                },
                lastName: {
                  value: c.lastName
                },
                middleName: {
                  value: c.middleName
                },
                gender: {
                  value: c.gender
                },
                civilStatus: {
                  value: c.civilStatus
                },
                religion: {
                  value: c.religion
                },
                mobileNumber: {
                  value: c.mobileNumber
                },
                emailAddress: {
                  value: c.emailAddress
                },
                placeOfBirth: {
                  value: c.placeOfBirth
                },
                dateOfBirth: {
                  value: c.dateOfBirth ? moment(c.dateOfBirth).format(DateTime.S) : ''
                },
                address: {
                  value: c.address
                },
                sssNo: {
                  value: c.sssNo
                },
                pagibigNo: {
                  value: c.pagibigNo
                },
                philhealthNo: {
                  value: c.philhealthNo
                },
                tinNo: {
                  value: c.tinNo
                },
                motherName: {
                  value: c.motherName
                },
                fatherName: {
                  value: c.fatherName
                },
                work: {
                  value: c.work
                },
                educationalAttainment: {
                  value: c.educationalAttainment
                },
                spouseName: {
                  value: c.spouseName
                },
                spouseWork: {
                  value: c.spouseWork
                },
                emergencyContactNumber: {
                  value: c.emergencyContactNumber
                },
                emergencyContactRelationship: {
                  value: c.emergencyContactRelationship
                },
                emergencyContactAddress: {
                  value: c.emergencyContactAddress
                },
                emergencyContactName: {
                  value: c.emergencyContactName
                },
                type: {
                  value: c.type
                },
                status: {
                  value: c.status
                },
                bodApprovalDate: {
                  value: c.bodApprovalDate ? moment(c.bodApprovalDate).format(DateTime.S) : ''
                },
                membershipId: {
                  value: c.membershipId
                },
                boardResolutionNumber: {
                  value: c.boardResolutionNumber
                }
              }
            });
          }
          // disabled: deactivated
        };
        options.push(
          {
            content: 'View Account',
            onClick: () => {
              history.push({
                pathname: Path.MemberAccount,
                search: `?cifNumber=${c.cifNumber}&customerId=${c.customerId}&name=${c.firstName} ${c.lastName}`
              });
            }
          },
          editDetails
        );

        if (localStorage.getItem('role') && localStorage.getItem('role') === UserRole.Admin) {
          deactivateUser = {
            content: c.status === CustomerStatus.Active ? locale.deactivate : locale.activate,
            onClick: () => {
              changeStatusCb(c.customerId, customerName, c.status);
            }
            // disabled: deactivated
          };
          options.push(deactivateUser);
        }

        map.set('options', <PopOverMenu options={options} />);
        return map;
      });
      return preparedData;
    }
    return [];
  }, [getCustomerResult, customerFormModal, changeStatusCb, history]);

  const onStatusChangeCb = useCallback(
    (status) => {
      const { requestState } = modifyFilters({ status, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onSearchCb = useCallback(
    (searchKey) => {
      const { requestState } = modifyFilters({ searchKey, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onDownloadFileCb = async () => {
    const { requestState } = modifyFilters({ reportType: 'customer' });

    delete requestState.perPage;
    delete requestState.page;

    const generateResult = await generateReportRequest(requestState);
    if (generateResult?.downloadUrl) {
      window.open(generateResult?.downloadUrl, '_blank');
    }
  };

  return (
    <div>
      <CustomerForm
        {...customerFormModal}
        refreshPage={() => {
          fetchCustomers(requestState);
        }}
      />
      <CustomerAccountForm
        {...customerAccountFormModal}
        refreshPage={() => {
          fetchCustomers(requestState);
        }}
      />
      <div>
        <Intro
          title={'Member List'}
          subtitle={locale.manageYourCustomers}
          actionText={'Add Member'}
          actionOnClick={() => {
            customerFormModal.show({
              title: 'Add Member',
              actionText: 'Add Member',
              create: true,
              initialState: {
                firstName: {
                  value: ''
                },
                lastName: {
                  value: ''
                },
                employeeId: {
                  value: '',
                  disabled: false
                },
                mobileNumber: {
                  value: ''
                }
              }
            });
          }}
          // actionDisabled={}
        />
      </div>
      <div className={styles.filters}>
        <div>
          <CustomerFilter
            filterState={filterState}
            onStatusChange={onStatusChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
            onDownloadFile={onDownloadFileCb}
            fetchingReport={fetchingReport}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingCustomer}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={customerColumns}
          data={preparedDriversData}
          dataCount={getCustomerResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
        />
      </div>
    </div>
  );
};

export default CustomerModule;
